import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { fmtNum, fmtMoney } from "@/data/gameData";
import { ArrowLeft, Loader2, Trophy, Crown, Medal, Sparkles } from "lucide-react";

const CAT_EMOJI = {
  dinheiro: "💰", carreira: "💼", negocios: "🏢", relacionamentos: "❤️",
  viagens: "🌎", fama: "🏆", sobrevivencia: "🚨", aleatorio: "🎲",
};

const rankStyle = (rank) => {
  if (rank === 1) return { color: "#FACC15", bg: "from-[#FACC15]/20 to-transparent", icon: Crown };
  if (rank === 2) return { color: "#CBD5E1", bg: "from-[#CBD5E1]/15 to-transparent", icon: Medal };
  if (rank === 3) return { color: "#F59E0B", bg: "from-[#F59E0B]/15 to-transparent", icon: Medal };
  return { color: "#64748B", bg: "", icon: null };
};

export default function Ranking() {
  const { http } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/leaderboard").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [http]);

  const entries = data?.entries || [];

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-2xl mx-auto px-5 py-8">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>

        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl grid place-items-center bg-[#FACC15]/15 text-[#FACC15]">
            <Trophy className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-display font-black text-3xl tracking-tighter leading-none">Ranking Global</h1>
            <p className="text-sm text-slate-400 mt-1">As melhores pontuações de todos os jogadores</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F43F5E]" /></div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-400">Ninguém no ranking ainda. Seja o primeiro!</p>
            <button onClick={() => navigate("/historias")} className="mt-4 px-6 py-3 rounded-2xl bg-[#F43F5E] font-semibold text-white">Jogar agora</button>
          </div>
        ) : (
          <div className="space-y-2.5 mt-8">
            {entries.map((e, i) => {
              const s = rankStyle(e.rank);
              const Icon = s.icon;
              return (
                <motion.button
                  key={e.session_id || i} data-testid={`rank-row-${e.rank}`}
                  initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                  onClick={() => navigate(`/resultado/${e.session_id}`)}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl border text-left transition-colors hover:bg-white/[0.07] ${
                    e.is_me ? "border-[#F43F5E]/50 bg-[#F43F5E]/10" : "border-white/10 bg-[#0F0F14]"
                  } ${s.bg ? `bg-gradient-to-r ${s.bg}` : ""}`}
                >
                  <div className="w-9 shrink-0 flex items-center justify-center">
                    {Icon ? <Icon className="w-6 h-6" style={{ color: s.color }} />
                      : <span className="font-display font-black text-lg" style={{ color: s.color }}>{e.rank}</span>}
                  </div>
                  <span className="text-2xl shrink-0">{e.persona?.emoji || CAT_EMOJI[e.category] || "🎲"}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate flex items-center gap-2">
                      {e.name}
                      {e.is_me && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F43F5E] text-white">VOCÊ</span>}
                    </p>
                    <p className="text-xs text-slate-400 truncate">
                      {CAT_EMOJI[e.category]} {e.persona?.nome || "—"} · {fmtMoney(e.money)}
                    </p>
                  </div>
                  <span className="font-display font-black text-xl text-[#F43F5E] shrink-0">{fmtNum(e.score)}</span>
                </motion.button>
              );
            })}
          </div>
        )}

        {!loading && data?.my_score != null && (
          <div data-testid="ranking-my-best" className="mt-6 rounded-2xl p-5 bg-gradient-to-br from-[#F43F5E]/15 to-[#8B5CF6]/10 border border-[#F43F5E]/25 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#F43F5E]" />
            <div className="flex-1">
              <p className="text-sm text-slate-300">Sua melhor pontuação</p>
              <p className="font-display font-black text-2xl">{fmtNum(data.my_score)} <span className="text-sm text-slate-400 font-body font-normal">· posição #{data.my_rank}</span></p>
            </div>
            <button onClick={() => navigate("/historias")} className="px-4 py-2.5 rounded-xl bg-[#F43F5E] text-white text-sm font-semibold shrink-0">Subir no ranking</button>
          </div>
        )}
      </div>
    </div>
  );
}
